import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

import { ErrorsNavigationContextProvider } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContextProvider';
import { useMessage } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { WebEDIError } from 'services/webediErrors/types';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { TransactionsRoutes } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { Page } from 'components/Page/Page';
import { Placeholder } from 'components/Placeholder/Placeholder';
import { MessageFieldErrorModal } from 'components/WebEDI/MessageFieldErrorModal/MessageFieldErrorModal';
import { WebEDISidebar } from 'components/WebEDI/WebEDISidebar/WebEDISidebar';

import { DroppedMessageAlert } from '../DroppedMessageAlert/DroppedMessageAlert';
import { MessageHeader } from './MessageHeader/MessageHeader';

export const UnknownTypePlaceholder = ({ messageId }: { messageId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentPartner } = useCurrentPartner();
  const { data, isLoading } = useMessage({
    variables: {
      messageId,
    },
  });

  const message = data?.data;
  const relatedTransactions = data?.related_messages;

  return (
    <ErrorsNavigationContextProvider<WebEDIError>
      service={WebEDIErrorsServiceSingleton}
      ErrorModalComponent={MessageFieldErrorModal}
    >
      <Page isLoading={isLoading}>
        <Page.Head
          rightContent={
            message ? (
              <MessageHeader
                message={message}
                messageOriginApp={null}
                relatedTransactions={relatedTransactions || []}
                currentPartner={currentPartner}
                mode={DisplayModes.view}
              />
            ) : null
          }
        />
        <Page.Section
          rightAside={
            <WebEDISidebar
              document={message}
              documentOriginApp={null}
              relatedTransactions={relatedTransactions}
              currentPartner={currentPartner}
            />
          }
        >
          <DroppedMessageAlert message={message} />

          <Placeholder
            title={t('webedi:viewMessage.fallback.placeholder.title')}
            description={t('webedi:viewMessage.fallback.placeholder.description', {
              externalRef: message?.external_message_ref,
            })}
            ctaOnClick={() => {
              navigate(routeToPage(TransactionsRoutes.root));
            }}
            ctaText={t('webedi:viewMessage.fallback.placeholder.cta')}
            ctaIcon={ArrowLeftIcon}
            icon={<EyeSlashIcon strokeWidth="1" />}
          />
        </Page.Section>
      </Page>
    </ErrorsNavigationContextProvider>
  );
};
